import { useMemo } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buildIcs,
  createCalendarTask,
  deleteCalendarObject,
  discoverCalendars,
  fetchCalendarObjects,
  getCalendarFeed,
  getCalendarItemDetail,
  newUid,
  objectHref,
  parseIcs,
  putCalendarObject,
} from "@api";
import { cancelScheduledAction } from "@api/actionsController";
import { fetchCustomerById } from "@api/customersController";

// The REST detail endpoint keys on the internal item id. That id is carried in the
// deep-link URL (/calendar/items/{id}). The CalDAV UID only equals it for
// service-projected items — items authored in the portal get a fresh internal id and
// keep the client UID as an external ref — so prefer the URL, fall back to the UID.
const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const guidIn = (value) => (value ?? "").match(GUID_RE)?.[0] ?? null;
export const resolveItemId = (item) => guidIn(item?.url) ?? guidIn(item?.uid);

const keys = {
  feed: ["calendar", "feed"],
  calendars: (accountRoot) => ["caldav", "calendars", accountRoot ?? "self"],
  objects: (calendarHref) => ["caldav", "objects", calendarHref],
  userObjects: (userId) => ["caldav", "user-objects", userId],
};

// A colleague's calendar is served at /api/caldav/u/{userId}/; the caller's own stays at the default
// account root. The server rewrites all hrefs onto whichever root we discover against.
export const accountRootForUser = (userId) => `/api/caldav/u/${userId}/`;

export const useCalendarFeed = () =>
  useQuery({
    queryKey: keys.feed,
    queryFn: getCalendarFeed,
    staleTime: Infinity,
  });

export const useCalendars = (accountRoot) =>
  useQuery({
    queryKey: keys.calendars(accountRoot),
    queryFn: () => discoverCalendars(accountRoot),
    staleTime: 5 * 60_000,
  });

/**
 * The overlay "team view": for each selected colleague, discover their shared calendar and fetch its
 * items in one query, tagged with the owner so the grid can colour and lock them. One query per
 * source (via useQueries) so a slow or failing colleague never blocks the others or blanks the grid.
 * `sources` is [{ userId, accountRoot }]; `meId` marks which items stay editable.
 */
export const useTeamCalendarItems = (sources, meId) =>
  useQueries({
    queries: sources.map(({ userId, accountRoot }) => ({
      queryKey: keys.userObjects(userId),
      queryFn: async () => {
        const calendars = await discoverCalendars(accountRoot);
        const href = calendars?.[0]?.href;
        if (!href) return [];
        const objects = await fetchCalendarObjects(href);
        return objects.flatMap(({ href: itemHref, etag, data }) =>
          parseIcs(data).map((item) => ({
            ...item,
            href: itemHref,
            etag,
            ownerUserId: userId,
            readOnly: userId !== meId,
          })),
        );
      },
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    })),
  });

/**
 * Full detail (real description, etc.) for one item, fetched from the calendar
 * REST API — the CalDAV feed redacts the description on projected items.
 */
export const useCalendarItemDetail = (item, enabled = true) => {
  const id = resolveItemId(item);
  return useQuery({
    queryKey: ["calendar", "item-detail", id],
    queryFn: ({ signal }) => getCalendarItemDetail(id, { signal }),
    enabled: enabled && !!id,
    staleTime: 30_000,
  });
};

/**
 * All items in a calendar collection, fetched via a CalDAV calendar-query
 * REPORT and parsed from ICS. Each item carries its resource href + etag so
 * edits can be written back with optimistic concurrency.
 */
export const useCalendarItems = (calendarHref) =>
  useQuery({
    queryKey: keys.objects(calendarHref),
    queryFn: async () => {
      const objects = await fetchCalendarObjects(calendarHref);
      return objects.flatMap(({ href, etag, data }) =>
        parseIcs(data).map((item) => ({ ...item, href, etag })),
      );
    },
    enabled: !!calendarHref,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

/**
 * Resolves customer account codes for a set of customer ids in one place, so the grid can show a code
 * on every item (tasks only carry the customer id on the feed — not the account code — so it has to be
 * looked up). Deduped and cached by id (shared with useCustomerDetails' ["customer", id] key), and
 * exposes a single isLoading so the page can hold its loading state until every code is in rather than
 * letting them pop in after the grid paints. Returns { map: { [customerId]: accountCode }, isLoading }.
 */
export const useCustomerAccountCodes = (customerIds) => {
  const unique = useMemo(
    () => [...new Set((customerIds ?? []).filter(Boolean))],
    [customerIds],
  );
  const results = useQueries({
    queries: unique.map((id) => ({
      queryKey: ["customer", id],
      queryFn: ({ signal }) => fetchCustomerById(id, { signal }),
      staleTime: 60_000,
      retry: 1,
    })),
  });
  const isLoading = results.some((result) => result.isLoading);
  // Rebuild the map only when a resolved code actually changes (useQueries hands back a fresh array
  // every render), so downstream memos don't churn on every parent render.
  const signal = unique.map((id, i) => `${id}:${results[i]?.data?.accountCode ?? ""}`).join("|");
  const map = useMemo(() => {
    const next = {};
    unique.forEach((id, i) => {
      next[id] = results[i]?.data?.accountCode ?? null;
    });
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signal]);
  return { map, isLoading };
};

export const useSaveCalendarItem = (calendarHref) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ item }) => {
      const uid = item.uid ?? newUid();
      const href = item.href ?? objectHref(calendarHref, uid);
      await putCalendarObject(href, buildIcs({ ...item, uid }), item.etag ?? null);
      return { uid, href };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.objects(calendarHref) });
      // The REST detail (full description etc.) is a separate cache from the CalDAV objects — refresh
      // it too, or an edit (e.g. the description) won't show when the item is reopened.
      queryClient.invalidateQueries({ queryKey: ["calendar", "item-detail"] });
      // A saved task may change its due date or status and move in/out of "today" — refresh the
      // Today's tasks query so the page list and the sidebar count badge (both read this cache) update.
      queryClient.invalidateQueries({ queryKey: ["todaysTasks"] });
    },
  });
};

export const useCreateCalendarTask = (calendarHref) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCalendarTask,
    onSuccess: (_, { assigneeUserId }) => {
      queryClient.invalidateQueries({ queryKey: keys.objects(calendarHref) });
      if (assigneeUserId) {
        queryClient.invalidateQueries({ queryKey: keys.userObjects(assigneeUserId) });
      }
      queryClient.invalidateQueries({ queryKey: ["todaysTasks"] });
    },
  });
};

export const useDeleteCalendarItem = (calendarHref) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ item }) => deleteCalendarObject(item.href, item.etag ?? null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.objects(calendarHref) });
      // Deleting a task removes it from Today's tasks — keep the page list and sidebar count in sync.
      queryClient.invalidateQueries({ queryKey: ["todaysTasks"] });
    },
  });
};

/**
 * Cancels the action behind a projected item (payment request / plan) on the action-service.
 * The projector cancels the calendar item(s) asynchronously off the action's lifecycle event,
 * so an immediate refetch would still show them — instead the clicked item is dropped from the
 * cache right away (a plan's sibling instalments catch up on the 60s poll), and the item's REST
 * detail is invalidated for whoever reopens it.
 */
export const useCancelProjectedAction = (calendarHref) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ actionId }) => cancelScheduledAction(actionId),
    onSuccess: (_, { item }) => {
      if (item?.uid) {
        queryClient.setQueryData(keys.objects(calendarHref), (cached) =>
          cached?.filter((entry) => entry.uid !== item.uid),
        );
      }
      queryClient.invalidateQueries({ queryKey: ["calendar", "item-detail"] });
    },
  });
};
